import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/api";

interface PasswordProtectionProps {
  children: React.ReactNode;
}

export function PasswordProtection({ children }: PasswordProtectionProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const validatePassword = useMutation(api.auth.validatePassword);
  const validateSession = useQuery(
    api.auth.validateSession,
    sessionId ? { sessionId } : "skip"
  );

  useEffect(() => {
    // Check if user has a valid session
    const storedSessionId = sessionStorage.getItem("sessionId");
    if (storedSessionId) {
      setSessionId(storedSessionId);
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sessionId && validateSession !== undefined) {
      if (validateSession) {
        setIsAuthenticated(true);
      } else {
        // Session is invalid, clear it
        sessionStorage.removeItem("sessionId");
        setSessionId(null);
      }
      setIsLoading(false);
    }
  }, [sessionId, validateSession]);

  const handlePasswordSubmit = useCallback(async () => {
    const password = "cali";

    if (password === null) {
      // User cancelled the prompt
      alert("Access denied. Password is required to use this application.");
      handlePasswordSubmit(); // Ask again
      return;
    }

    try {
      // Validate password on the server and get session
      const result = await validatePassword({ password });

      if (result.isValid && result.sessionId) {
        // Store session ID locally
        sessionStorage.setItem("sessionId", result.sessionId);
        setSessionId(result.sessionId);
        setIsAuthenticated(true);
      } else {
        // User entered a password but it was wrong
        alert("Incorrect password. Please try again.");
        handlePasswordSubmit(); // Ask again
      }
    } catch (error) {
      console.error("Authentication failed:", error);
      alert("Authentication failed. Please try again.");
      handlePasswordSubmit(); // Ask again
    }
  }, [validatePassword]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !sessionId) {
      handlePasswordSubmit();
    }
  }, [isLoading, isAuthenticated, sessionId, handlePasswordSubmit]);

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
          color: "#666",
        }}
      >
        Loading...
      </div>
    );
  }

  return <>{children}</>;
}
